import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip, CircularProgress, TextField, InputAdornment, Grid, MenuItem, Select, FormControl, InputLabel, TablePagination, Tabs, Tab, Tooltip, IconButton, Collapse } from '@mui/material';
import { Search, Refresh, FileDownload, CallReceived, CallMade, BugReport, FilterList, Clear } from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore, financialHidden } from '../../store/auth';
import { SipTraceModal } from '../../components/SipTraceModal';
import { COUNTRY_LIST } from '../../utils/dialCountry';

const STATUS_COLOR: any = { answered: 'success', failed: 'error', busy: 'warning', noanswer: 'default', cancelled: 'default' };

const EMPTY_FILTERS = {
  status: '', direction: '', src: '', dst: '', tenant_id: '', country_code: '',
  hangup_code: '', dur_min: '', dur_max: '', sip_call_id: '', call_id: '', codec: '', dst_original: '', from: '', to: '',
};

export const CdrPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<any>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [advOpen, setAdvOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirTab, setDirTab] = useState(0);
  const [traceId, setTraceId] = useState<string | null>(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);

  useEffect(() => { api.get('/tenants?limit=500&tier=customer').then(({ data: r }) => setTenants(r.data || [])).catch(() => {}); }, []);

  const activeParams = () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(page * 50), ...activeParams() });
      const summaryParams = new URLSearchParams(activeParams());   // summary must reflect the SAME filters
      const [cdrRes, sumRes] = await Promise.all([
        api.get('/cdr?' + params),
        api.get('/cdr/summary?' + summaryParams),
      ]);
      setData(cdrRes.data.data || []);
      setTotal(cdrRes.data.total || 0);
      setSummary(sumRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, filters]);

  const set = (k: string, v: string) => { setFilters({ ...filters, [k]: v }); setPage(0); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: '5000', offset: '0', ...activeParams() });
      const { data: res } = await api.get('/cdr?' + params);
      const rows: any[] = res.data || [];
      const cols = ['start_time', 'direction', 'customer_name', 'src_number', 'dst_number', 'dst_original', 'carrier', 'duration', 'billable_duration', 'status', 'hangup_code', 'hangup_reason', 'sip_call_id', 'call_id', 'codec', 'mos', ...(hideMoney ? [] : ['sell_cost'])];
      const header = ['Start Time', 'Direction', 'Customer', 'From', 'To', 'Original Dialed', 'Carrier', 'Duration(s)', 'Billable(s)', 'Status', 'SIP Code', 'Reason', 'SIP Call-ID', 'Call-ID', 'Codec', 'MOS', ...(hideMoney ? [] : ['Amount'])];
      const esc = (v: any) => { const s = (v === null || v === undefined) ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
      const csv = [header.join(',')]
        .concat(rows.map(r => cols.map(c => esc(c === 'start_time' && r[c] ? new Date(r[c]).toISOString() : r[c])).join(',')))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `cdr-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const fmtDur = (s: number) => s >= 60 ? Math.floor(s / 60) + 'm ' + (s % 60) + 's' : (s || 0) + 's';
  const hideMoney = financialHidden(useAuthStore.getState().user?.role);
  const fmtMoney = (n: any) => (n === null || n === undefined) ? '—' : '$' + (parseFloat(n) || 0).toFixed(4);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h5' fontWeight={700}>CDR Reports</Typography>
        <Button variant='outlined' startIcon={exporting ? <CircularProgress size={16} /> : <FileDownload />} onClick={exportCsv} disabled={exporting}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </Box>

      <Grid container spacing={2} mb={2}>
        {[
          { label: 'Total Calls', value: summary?.total_calls || 0 },
          { label: 'Answered', value: summary?.answered_calls || 0 },
          { label: 'ASR', value: (summary?.asr || 0) + '%' },
          ...(hideMoney ? [] : [{ label: 'Revenue', value: fmtMoney(summary?.total_revenue) },
          { label: 'Margin', value: fmtMoney(summary?.total_margin) }]),
          { label: 'Avg MOS', value: summary?.avg_mos || '-' },
        ].map(s => (
          <Grid item xs={6} sm={4} md={2} key={s.label}>
            <Card><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant='caption' color='text.secondary'>{s.label}</Typography>
              <Typography variant='h6' fontWeight={700}>{String(s.value)}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <Tabs value={dirTab} onChange={(_, v) => { setDirTab(v); setFilters({ ...filters, direction: ['', 'inbound', 'outbound'][v] }); setPage(0); }} sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tab label='All Calls' />
          <Tab icon={<CallReceived fontSize='small' />} iconPosition='start' label='Inbound' />
          <Tab icon={<CallMade fontSize='small' />} iconPosition='start' label='Outbound' />
        </Tabs>
        <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <TextField size='small' label='Caller' placeholder='Source number' value={filters.src} onChange={e => set('src', e.target.value)} sx={{ width: 150 }} />
          <TextField size='small' label='Callee' placeholder='Destination' value={filters.dst} onChange={e => set('dst', e.target.value)} sx={{ width: 150 }} />
          <FormControl size='small' sx={{ minWidth: 190 }}>
            <InputLabel>Customer</InputLabel>
            <Select value={filters.tenant_id} label='Customer' onChange={e => set('tenant_id', e.target.value)}>
              <MenuItem value=''>All customers</MenuItem>
              {tenants.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel>Country</InputLabel>
            <Select value={filters.country_code} label='Country' onChange={e => set('country_code', e.target.value)}>
              <MenuItem value=''>All countries</MenuItem>
              {COUNTRY_LIST.map((c) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size='small' sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label='Status' onChange={e => set('status', e.target.value)}>
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='answered'>Answered</MenuItem>
              <MenuItem value='failed'>Failed</MenuItem>
              <MenuItem value='busy'>Busy</MenuItem>
              <MenuItem value='noanswer'>No Answer</MenuItem>
            </Select>
          </FormControl>
          <Button variant='text' startIcon={<FilterList />} onClick={() => setAdvOpen(o => !o)}>{advOpen ? 'Fewer filters' : 'More filters'}</Button>
          <Button variant='outlined' startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant='text' color='inherit' startIcon={<Clear />} onClick={() => { setFilters({ ...EMPTY_FILTERS }); setDirTab(0); setPage(0); }}>Clear</Button>
        </Box>
        <Collapse in={advOpen}>
          <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
            <TextField size='small' type='datetime-local' label='From' InputLabelProps={{ shrink: true }} value={filters.from} onChange={e => set('from', e.target.value)} sx={{ width: 210 }} />
            <TextField size='small' type='datetime-local' label='To' InputLabelProps={{ shrink: true }} value={filters.to} onChange={e => set('to', e.target.value)} sx={{ width: 210 }} />
            <TextField size='small' label='Termination code' placeholder='e.g. 486' value={filters.hangup_code} onChange={e => set('hangup_code', e.target.value)} sx={{ width: 150 }} />
            <TextField size='small' label='Hold time ≥ (s)' type='number' value={filters.dur_min} onChange={e => set('dur_min', e.target.value)} sx={{ width: 130 }} />
            <TextField size='small' label='Hold time ≤ (s)' type='number' value={filters.dur_max} onChange={e => set('dur_max', e.target.value)} sx={{ width: 130 }} />
            <TextField size='small' label='Original dialed' value={filters.dst_original} onChange={e => set('dst_original', e.target.value)} sx={{ width: 150 }} />
            <TextField size='small' label='SIP Call-ID' value={filters.sip_call_id} onChange={e => set('sip_call_id', e.target.value)} sx={{ width: 200 }} />
            <TextField size='small' label='Internal Call-ID' value={filters.call_id} onChange={e => set('call_id', e.target.value)} sx={{ width: 200 }} />
            <TextField size='small' label='Codec' value={filters.codec} onChange={e => set('codec', e.target.value)} sx={{ width: 120 }} />
          </Box>
        </Collapse>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Call ID</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Carrier</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reason</TableCell>
              {!hideMoney && <TableCell>Revenue</TableCell>}
              <TableCell>MOS</TableCell>
              <TableCell align='center'>Trace</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={13} align='center'><CircularProgress size={24} /></TableCell></TableRow> :
              data.length === 0 ? <TableRow><TableCell colSpan={13} align='center' sx={{ py: 4, color: 'text.secondary' }}>No CDRs found</TableCell></TableRow> :
              data.map((r, i) => (
                <TableRow key={r.id || i} hover>
                  <TableCell><Typography variant='caption' fontFamily='monospace'>{r.call_id?.substring(0, 12)}...</Typography></TableCell>
                  <TableCell><Chip label={r.direction} size='small' variant='outlined' /></TableCell>
                  <TableCell><Typography variant='body2' fontWeight={600}>{r.customer_name || '-'}</Typography></TableCell>
                  <TableCell><Typography variant='body2' fontWeight={500}>{r.src_number || '-'}</Typography></TableCell>
                  <TableCell><Typography variant='body2' fontWeight={500}>{r.dst_number || '-'}</Typography></TableCell>
                  <TableCell>{r.carrier || r.carrier_name || '-'}</TableCell>
                  <TableCell><Typography variant='caption'>{new Date(r.start_time).toLocaleString()}</Typography></TableCell>
                  <TableCell>{fmtDur(r.duration)}</TableCell>
                  <TableCell><Chip label={r.status} size='small' color={STATUS_COLOR[r.status] || 'default'} /></TableCell>
                  <TableCell>
                    <Tooltip title={r.hangup_code ? 'SIP ' + r.hangup_code : ''}>
                      <Typography variant='caption' sx={{ color: r.status === 'answered' ? 'text.secondary' : 'error.main' }}>{r.hangup_reason || '-'}</Typography>
                    </Tooltip>
                  </TableCell>
                  {!hideMoney && <TableCell>{fmtMoney(r.sell_cost)}</TableCell>}
                  <TableCell>{r.mos || '-'}</TableCell>
                  <TableCell align='center'>
                    <Tooltip title='SIP ladder'>
                      <IconButton size='small' onClick={() => { setTraceId(r.call_id); setTraceOpen(true); }} disabled={!r.call_id}>
                        <BugReport fontSize='small' sx={{ color: '#00b0ff' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
        <TablePagination component='div' count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} rowsPerPageOptions={[50]} />
      </Card>
      <SipTraceModal callId={traceId} open={traceOpen} onClose={() => setTraceOpen(false)} />
    </Box>
  );
};
